import styled from '@emotion/styled';
import type { EntityEvent } from '@racemap/sdk/schema/entityEvents';
import type { RacemapStarters } from '@racemap/utilities/types/events';
import { castDraft } from 'immer';
import { type FC, Suspense, useEffect, useState } from 'react';
import 'react-virtualized/styles.css';
import { lazily } from '../../lib/lazily';
import { useMonitorStore } from '../../store/monitor/monitorStore';
import { LoadingSpinner } from '../LoadingSpinner';
import { getFilteredStarters } from '../utils/helpers';
import { Header } from './Header';
import { MonitorTableAndStream } from './MonitorTableAndStream';

const { MonitorOverviewProgress } = lazily(() => import('./MonitorOverviewProgress'));
const { Toolbar } = lazily(() => import('./Toolbar'));
const { RacemapLogo } = lazily(() => import('../BasicComponents/RacemapLogo'));

export interface Column {
  key: string;
  title: string;
  width: number;
  render: (row: RacemapStarters) => React.ReactNode;
  sorter?: (a: RacemapStarters, b: RacemapStarters) => number;
}

export const Monitor: FC = () => {
  const starters = useMonitorStore((s) => s.starters);
  const alerts = useMonitorStore((s) => s.alerts);

  const [searchValue, setSearchValue] = useState('');
  const [showAlertStream, setShowAlertStream] = useState(false);
  const [lastTimeAlertStreamOpened, setLastTimeAlertStreamOpened] = useState<Date | null>(null);
  const [selectedAlert, setSelectedAlert] = useState<EntityEvent | null>(null);
  const filteredStarters = castDraft(getFilteredStarters(starters, searchValue, false));
  const alertsArray = Array.from(alerts.values()).sort(
    (a, b) => b.occurredAt.getTime() - a.occurredAt.getTime(),
  );
  const notificationsUnread = areNotificationsUnread(alertsArray[0], lastTimeAlertStreamOpened);

  useEffect(() => {
    if (!showAlertStream) {
      setSelectedAlert(null);
    }
  }, [showAlertStream]);

  return (
    <Container>
      <Header />
      <Suspense fallback={<LoadingSpinner />}>
        <div className="monitor-overview">
          <MonitorOverviewProgress starters={starters} />
        </div>
        <Toolbar
          searchValue={searchValue}
          showAlertStream={showAlertStream}
          alertNotification={notificationsUnread}
          onSearchValueChange={setSearchValue}
          onShowAlertStreamChange={setShowAlertStream}
          onAlertStreamOpened={() => setLastTimeAlertStreamOpened(new Date())}
        />
        <MonitorTableAndStream
          showStream={showAlertStream}
          filteredStarters={filteredStarters}
          onAlertSelection={setSelectedAlert}
          selectedAlert={selectedAlert}
        />
        <div className="monitor-footer">
          <RacemapLogo height={40} />
        </div>
      </Suspense>
    </Container>
  );
};

const areNotificationsUnread = (
  lastAlert: EntityEvent | null,
  lastReadAt: Date | null,
): boolean => {
  if (lastAlert == null) return false;
  if (lastReadAt == null) return true;
  return lastReadAt.getTime() < lastAlert.occurredAt.getTime();
};

const Container = styled.div`
  background: white;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .monitor-overview {
    height: 80px;
    margin: 15px 30px 5px 30px;
  }

  .monitor-footer {
    padding-left: 30px;
    display: flex;
  }
`;
